import { type FuelType } from "@/hooks/use-fuel";
import { type VehicleType } from "@/types/settings";
import { calculateTripCost, formatCurrency } from "@/lib/tripCost";
import { Navigation, MapPin, Award, Share2, Fuel, TrendingDown, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import { forwardRef } from "react";

export interface Station {
  code: string;
  name: string;
  brand: string;
  address: string;
  postcode: string | null;
  location: { lat: number; lng: number };
  prices: Record<string, { price: number; updated: string }>;
}

interface StationCardProps {
  station: Station;
  fuelType: FuelType;
  userLocation?: { lat: number; lng: number } | null;
  isBestValue?: boolean;
  index: number;
  vehicleType?: VehicleType;
  closestStationPrice?: number;
  closestStationDistance?: number;
  onStationClick?: (station: Station) => void;
  tankSize?: number;
  customConsumption?: number;
}

function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export const StationCard = forwardRef<HTMLDivElement, StationCardProps>(
  ({ station, fuelType, userLocation, isBestValue = false, index, vehicleType, closestStationPrice, closestStationDistance, onStationClick, tankSize, customConsumption }, ref) => {
    const price = station.prices[fuelType]?.price;
    const distance = userLocation
      ? calculateDistance(userLocation.lat, userLocation.lng, station.location.lat, station.location.lng)
      : undefined;

    // Calculate trip cost if we have all required data
    const tripCost = (price && distance !== undefined && vehicleType)
      ? calculateTripCost(price, distance, vehicleType, closestStationPrice, closestStationDistance, tankSize, customConsumption)
      : null;

    const hasTripCost = tripCost && distance !== undefined && distance > 0.1;

    const variants = {
      hidden: { opacity: 0, y: 20 },
      visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.05 + 0.3, duration: 0.4 }
      })
    };

    return (
      <motion.div
        ref={ref}
        custom={index}
        initial="hidden"
        animate="visible"
        variants={variants}
        data-testid={`station-card-${station.code}`}
        onClick={() => onStationClick?.(station)}
        className={`
          relative mx-4 mb-4 rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer hover:bg-white/5
          lg:mx-0 lg:mb-0 lg:rounded-xl lg:hover:bg-white/5
          ${isBestValue
            ? "bg-gradient-to-br from-slate-800 to-slate-900 border border-emerald-500/30 shadow-lg"
            : "glass-card"}
        `}
      >
        {/* Best Value banner — mobile only */}
        {isBestValue && (
          <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-4 py-1.5 flex items-center gap-2 lg:hidden">
            <Award size={14} className="text-emerald-400" />
            <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Best Value Nearby</span>
          </div>
        )}

        <div className="p-5 flex items-center justify-between gap-4 lg:py-3 lg:px-5 lg:gap-6">
          {/* Rank / badge — desktop only */}
          <div className="hidden lg:flex items-center justify-center flex-shrink-0 w-8">
            {isBestValue ? (
              <Award size={18} className="text-emerald-400" />
            ) : (
              <span className="text-sm font-bold text-muted-foreground">#{index + 1}</span>
            )}
          </div>

          {/* Station name + brand */}
          <div className="flex-1 min-w-0 lg:flex-none lg:w-44">
            <div className="flex items-baseline gap-2 mb-1 lg:mb-0">
              <h3 className="font-bold text-lg text-foreground truncate lg:text-sm">{station.name}</h3>
            </div>
            <span className="hidden lg:block text-xs text-muted-foreground truncate">{station.brand}</span>

            {/* Mobile: distance + address row */}
            <div className="flex items-center gap-4 text-muted-foreground text-xs font-medium flex-wrap lg:hidden">
              {distance !== undefined && (
                <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-md">
                  <MapPin size={12} />
                  <span>{distance.toFixed(1)} km</span>
                </div>
              )}
              <div className="text-slate-500 truncate">{station.address}</div>
            </div>
          </div>

          {/* Address — desktop only */}
          <div className="hidden lg:block lg:flex-1 lg:min-w-0">
            <span className="text-xs text-muted-foreground truncate block">{station.address}</span>
          </div>

          {/* Distance — desktop only */}
          {distance !== undefined && (
            <div className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
              <MapPin size={12} />
              <span>{distance.toFixed(1)} km</span>
            </div>
          )}

          {/* Price + action buttons */}
          <div className="flex flex-col items-end gap-3 flex-shrink-0 lg:flex-row lg:items-center lg:gap-6">
            <div className="text-right">
              {price ? (
                <>
                  <span className="font-mono text-[2rem] font-bold tracking-tighter leading-none text-foreground block lg:text-base lg:leading-normal">
                    {price.toFixed(1)}
                  </span>
                  <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">cents/L</span>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">No price</span>
              )}
            </div>

            {/* Share / Navigate buttons — mobile only */}
            <div className="flex items-center gap-2 lg:hidden" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={async () => {
                  const shareUrl = `${window.location.origin}/?station=${station.code}&fuel=${fuelType}`;
                  const shareText = price
                    ? `Found fuel at ${price.toFixed(1)}c/L at ${station.name}!`
                    : `Check out ${station.name} on AussiePetrol!`;

                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: 'AussiePetrol - Cheap Fuel Alert',
                        text: shareText,
                        url: shareUrl,
                      });
                    } catch (err) {
                      console.log('Share cancelled or failed:', err);
                    }
                  } else {
                    navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                  }
                }}
                data-testid={`share-button-${station.code}`}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200 bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground hover:scale-105"
                aria-label="Share"
              >
                <Share2 size={16} strokeWidth={2.5} />
              </button>

              <button
                type="button"
                onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(`${station.name}, ${station.address}`)}`, '_blank')}
                data-testid={`navigate-button-${station.code}`}
                className={`
                  h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200
                  ${isBestValue
                    ? "bg-emerald-500 text-white shadow-lg hover:bg-emerald-400 hover:scale-105"
                    : "bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 hover:scale-105"}
                `}
                aria-label="Navigate"
              >
                <Navigation size={18} strokeWidth={2.5} className="-ml-0.5 mt-0.5" />
              </button>
            </div>
          </div>

          {/* Total cost — desktop only */}
          {hasTripCost && (
            <div className="hidden lg:block text-right flex-shrink-0 w-20">
              <span className="text-sm font-bold text-foreground">{formatCurrency(tripCost.totalCost)}</span>
              <span className="block text-[10px] text-muted-foreground">total</span>
            </div>
          )}
        </div>

        {/* Trip Cost Analysis — mobile only */}
        {hasTripCost && (
          <div className="px-5 pb-4 pt-0 lg:hidden">
            <div className="bg-white/5 rounded-lg p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Fuel size={12} />
                  <span>45L fill: {formatCurrency(tripCost.fuelCost)}</span>
                  <span className="text-white/30">+</span>
                  <span>{formatCurrency(tripCost.tripCost)} trip</span>
                </div>
                <div className="font-medium text-foreground">
                  Total: {formatCurrency(tripCost.totalCost)}
                </div>
              </div>

              {!tripCost.isClosest && tripCost.savingsVsClosest !== 0 && (
                <div className={`mt-2 flex items-center gap-1.5 ${
                  tripCost.isWorthIt ? "text-emerald-400" : "text-amber-400"
                }`}>
                  {tripCost.isWorthIt ? (
                    <>
                      <TrendingDown size={12} />
                      <span>Save {formatCurrency(Math.abs(tripCost.savingsVsClosest))} vs closest</span>
                    </>
                  ) : (
                    <>
                      <TrendingUp size={12} />
                      <span>Costs {formatCurrency(Math.abs(tripCost.savingsVsClosest))} more than closest</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    );
  }
);

StationCard.displayName = "StationCard";
